import { Component, Input, Output, EventEmitter, SimpleChanges, ViewChild, ElementRef } from "@angular/core"
import { FirestoreService } from "../firestore-service.service"
import { Comment } from "../container"
import { environment } from 'src/environments/environment';
import { ConfirmingDialogComponent } from "../confirming-dialog/confirming-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { AddcommentComponent } from "../addcomment/addcomment.component";
import { EmoticonsComponent } from "../emoticons/emoticons.component";

@Component({
    selector: 'app-commentlist',
    templateUrl: environment.site === 'wroolo' ? './commentlist.component.wroolo.html' : './commentlist.component.productly.html',
    styleUrl: './commentlist.component.scss',
    imports: [CommonModule, FormsModule, AddcommentComponent, EmoticonsComponent],
    standalone: true
})
export class CommentlistComponent {
  @Input() userId = ""
  @Input() userImageURL = ""
  @Input() projectKey = ""
  @Input() organizationKey = ""
  @Input() contentKey = ""
  @Input() projectName = ""
  @Input() userEmail = ""
  @Input() userName = ""
  @Input() isReadOnly = false
  @Input() canvasObjectKey = ""
  @Input() canvasObjectType = ""
  @Input() embeddingModel = "gemini-embedding-001"
  @Input() canAddComment = true
  @Output() onAddComment = new EventEmitter<Comment>()
  @Output() areCanvasEventsSuspended = new EventEmitter<boolean>();
  @Output() onCommentListExpanded = new EventEmitter<boolean>();
  @Output() isListening = new EventEmitter<boolean>();
  @Output() updateEmoticons = new EventEmitter<{canvasObjectKey: string, emoticons: string}>();
  comments: Comment[] = []
  isCommentListExpanded = false
  editedCommentKey = ""
  channelKey = ""
  channelName = ""
  channelPath = ""
  contentType = "canvasCard"
  addingToParentCommentKey = ""
  commentsCount = 0
  //pathSegments: string[] = []
  @ViewChild('commentListBody') commentListBody!: ElementRef; // Reference to the comment list body

  constructor(private firestore: FirestoreService, private dialog: MatDialog) {

  }

  ngAfterViewInit() {
    this.channelKey = this.contentKey
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['contentKey']) {
      this.contentKey = changes['contentKey'].currentValue
      this.getComments()
    }
    if (this.canvasObjectKey != "" && changes['canvasObjectKey']) {
      this.getComments()
      this.isCommentListExpanded = false
      this.addingToParentCommentKey = ""
      this.editedCommentKey = ""
    }
  }

  formatCommentDate(dateInput: Date | string): string {
    return this.firestore.formatCommentDate(dateInput)
  }

  formatAllComments() {
    this.comments.forEach(comment => {
      comment.formattedDate = this.formatCommentDate(comment.dateOfCreation)
    })
  }

  toggleCommentList() {
    this.isCommentListExpanded = !this.isCommentListExpanded;
    if (this.isCommentListExpanded) {
      setTimeout(() => {
        this.scrollToTarget();
      }, 100);
    }
    this.onCommentListExpanded.emit(this.isCommentListExpanded)
  }

  scrollToTarget() {
    if (this.commentListBody) {
      this.commentListBody.nativeElement.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  }

  async addComment(comment: Comment) {
    this.editedCommentKey = ""
    comment.formattedDate = this.formatCommentDate(comment.dateOfCreation)
    this.comments.unshift(comment)
    this.onAddComment.emit(comment)
    this.addingToParentCommentKey = ""
    this.comments = this.sortComments(this.comments)
    await this.firestore.incrementCommentCount(this.organizationKey, this.channelKey, this.projectKey, 1)
  }

  async getComments() {
    await this.firestore.getCommentsByContentKey(this.organizationKey, this.contentKey).then((comments) => {
      this.comments = this.sortComments(comments)
    });
    this.commentsCount = this.comments.length
    this.formatAllComments();
    return this.comments
  }

  sortComments(comments: Comment[]) {
          // Filter by canvasObjectKey if it's set
          let filteredComments = this.canvasObjectKey ? comments.filter(comment => comment.canvasObjectKey === this.canvasObjectKey) : comments;
  
          // Separate comments into parents and children
          const parentComments = filteredComments.filter(comment => !comment.parentCommentKey);
          const subComments = filteredComments.filter(comment => comment.parentCommentKey);
  
          // Sort parent comments by date descending
          parentComments.sort((a, b) => new Date(a.dateOfCreation).getTime() - new Date(b.dateOfCreation).getTime());
  
          // Attach subcomments to their respective parents
          parentComments.forEach(parent => {
              const children = subComments.filter(c => c.parentCommentKey === parent.key);
              // Optional: Sort subcomments
              children.sort((a, b) => new Date(a.dateOfCreation).getTime() - new Date(b.dateOfCreation).getTime());
              parent.children = children;
          });
  
          // Flatten the sorted and nested structure for display
          let finalDisplayList: Comment[] = [];
          parentComments.forEach(parent => {
              finalDisplayList.push(parent);
              if (parent.children) {
                  finalDisplayList = finalDisplayList.concat(parent.children);
              }
          });
  
          return finalDisplayList;
  }

  onCommentClicked(comment: Comment) {
   this.editedCommentKey = comment.key
   this.addingToParentCommentKey = ""
  }

  onCanvasEventsSuspended(event: boolean) {
    this.areCanvasEventsSuspended.emit(event)
  }

  onEditorClosed() {
    this.editedCommentKey = ""
  }

  onIsListening(event: boolean) {
    this.isListening.emit(event)
  }

  async addCommentToComment(parentCommentKey: string) {
    this.addingToParentCommentKey = parentCommentKey
    this.editedCommentKey = ""
    this.getComments()
  }

  onCommentUpdated(event: {commentKey: string, content: string}) {
    const index = this.comments.findIndex(comment => comment.key == event.commentKey)
    if (index != -1) {
      this.comments[index].content = event.content
      this.comments[index].dateOfUpdate = new Date()
      this.comments[index].formattedDate = this.formatCommentDate(this.comments[index].dateOfUpdate)
    }
    this.editedCommentKey = "" // to close the editor
    this.getComments()
  }

  async removeComment(commentKey: string) {
    const dialogConfirmingRef = this.dialog.open(ConfirmingDialogComponent, {
      data: {"content": "Are you sure to remove this comment?", "onlyCloseButton": false}
    });
    dialogConfirmingRef.afterClosed().subscribe(async (r) => {
      if (r == true) {
        //const pathSegments = this.createPathSegments(commentKey)
        await this.firestore.removeComment2(this.organizationKey, this.contentKey, this.channelKey, this.contentType, this.projectKey, commentKey)
        const index = this.comments.findIndex(comment => comment.key == commentKey)
        this.comments[index].isDeleted = true
        await this.firestore.incrementCommentCount(this.organizationKey, this.channelKey, this.projectKey, -1)
      }
    })
  }

  onUpdateEmoticons(event: {canvasObjectKey: string, emoticons: string}) {
    this.updateEmoticons.emit(event)
  }

}
